// Engagement buffer + flush (BUILD_PLAN.md §6 Phase 4, task step 2).
//
// The event-type vocabulary below is the CONTRACT with the downstream Parquet
// schema and the DuckDB queries on the home box. Do not add, rename, or remove
// types without coordinating the schema change end-to-end.
//
// Events are buffered locally and flushed to the Worker every 30 s or every 20
// events, whichever comes first. The payload carries the device id and a
// client timestamp; the Worker stamps the authoritative `ts_server` (we never
// trust the client clock server-side). Flushing is a no-op until the device is
// bound, so nothing is transmitted before consent.

import { apiUrl } from "../api/fetch";
import { getDeviceId } from "../identity/device";

/** The fixed event vocabulary. Contract with the Parquet schema and with the
 * edge Worker's `ENGAGEMENT_TYPES` (edge/src/queue.ts) — the two lists must
 * stay identical; `engagement.vocab.test.ts` pins them. */
export const ENGAGEMENT_TYPES = [
  "impression",
  "click",
  "dwell_30s",
  "dwell_120s",
  "like",
  "hide",
  "share",
] as const;

export type EngagementType = (typeof ENGAGEMENT_TYPES)[number];

export interface EngagementEvent {
  type: EngagementType;
  article_id: number;
  ts_client: number;
}

export interface EngagementPayload {
  device_id: string;
  events: EngagementEvent[];
}

export const FLUSH_INTERVAL_MS = 30_000;
export const FLUSH_AT_COUNT = 20;
/** Cap so an unbound (offline / pre-bind) session can't grow without bound. */
export const MAX_BUFFER = 500;

export type Sender = (payload: EngagementPayload) => Promise<void>;
export type DeviceIdProvider = () => Promise<string | null>;

interface Scheduler {
  set(fn: () => void, ms: number): ReturnType<typeof setTimeout>;
  clear(handle: ReturnType<typeof setTimeout>): void;
}

const realScheduler: Scheduler = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h),
};

export interface EngagementBufferOptions {
  flushIntervalMs?: number;
  flushAtCount?: number;
  maxBuffer?: number;
  now?: () => number;
  scheduler?: Scheduler;
}

/**
 * Local engagement buffer. Injectable sender / device-id provider / clock /
 * scheduler keep it unit-testable without timers or network.
 */
export class EngagementBuffer {
  private buf: EngagementEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly flushIntervalMs: number;
  private readonly flushAtCount: number;
  private readonly maxBuffer: number;
  private readonly now: () => number;
  private readonly scheduler: Scheduler;

  constructor(
    private readonly sender: Sender,
    private readonly deviceId: DeviceIdProvider,
    opts: EngagementBufferOptions = {},
  ) {
    this.flushIntervalMs = opts.flushIntervalMs ?? FLUSH_INTERVAL_MS;
    this.flushAtCount = opts.flushAtCount ?? FLUSH_AT_COUNT;
    this.maxBuffer = opts.maxBuffer ?? MAX_BUFFER;
    this.now = opts.now ?? (() => Date.now());
    this.scheduler = opts.scheduler ?? realScheduler;
  }

  /** Number of events currently buffered (test/inspection aid). */
  size(): number {
    return this.buf.length;
  }

  /** Queue an event. Triggers an immediate flush at the count threshold,
   * otherwise arms the interval timer. */
  record(
    type: EngagementType,
    articleId: number,
    tsClient: number = this.now(),
  ): void {
    this.buf.push({ type, article_id: articleId, ts_client: tsClient });
    // Drop oldest if we somehow exceed the cap (long offline session).
    if (this.buf.length > this.maxBuffer) {
      this.buf.splice(0, this.buf.length - this.maxBuffer);
    }
    if (this.buf.length >= this.flushAtCount) {
      void this.flush();
    } else {
      this.arm();
    }
  }

  private arm(): void {
    if (this.timer !== null) return;
    this.timer = this.scheduler.set(() => {
      this.timer = null;
      void this.flush();
    }, this.flushIntervalMs);
  }

  private disarm(): void {
    if (this.timer !== null) {
      this.scheduler.clear(this.timer);
      this.timer = null;
    }
  }

  /** Send everything buffered. No-op if empty or if the device isn't bound
   * yet (events stay buffered for a later flush). On send failure the batch is
   * re-queued at the front. */
  async flush(): Promise<void> {
    this.disarm();
    if (this.buf.length === 0) return;
    const device_id = await this.deviceId();
    if (!device_id) {
      // Not bound (e.g. bind pending or declined) — keep buffering.
      this.arm();
      return;
    }
    const batch = this.buf;
    this.buf = [];
    try {
      await this.sender({ device_id, events: batch });
    } catch {
      // Re-queue (oldest first) and let the next trigger retry.
      this.buf = batch.concat(this.buf);
      if (this.buf.length > this.maxBuffer) {
        this.buf.splice(0, this.buf.length - this.maxBuffer);
      }
      this.arm();
    }
  }

  /** Drop everything pending and cancel the timer (used by "Forget me"). */
  reset(): void {
    this.disarm();
    this.buf = [];
  }
}

const postEngagement: Sender = async (payload) => {
  const res = await fetch(apiUrl("/api/engagement"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    keepalive: true,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`engagement -> ${res.status} ${res.statusText}`);
  }
};

/** Process-wide buffer wired to the real Worker endpoint + device id. */
export const engagement = new EngagementBuffer(postEngagement, getDeviceId);

/** Flush remaining events on page hide so we don't lose the tail. */
export function installEngagementFlushHooks(): void {
  if (typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void engagement.flush();
  });
}
